/**
 * Presentation of the Scripts monitor's campaign statuses, and the merge of polled
 * log lines into what the terminal panel shows. Pure, so it is unit-tested
 * (tests/scriptStatus.test.js).
 */

/** Status -> label, Badge tone, icon. The server only ever sends these keys. */
export const SCRIPT_STATUS = Object.freeze({
  running: { label: 'Running', tone: 'info', icon: 'fa-circle-play' },
  stopping: { label: 'Stopping', tone: 'warning', icon: 'fa-spinner fa-spin' },
  stopped: { label: 'Stopped', tone: 'neutral', icon: 'fa-circle-stop' },
  completed: { label: 'Completed', tone: 'success', icon: 'fa-circle-check' },
  failed: { label: 'Failed', tone: 'danger', icon: 'fa-circle-xmark' },
  paused: { label: 'Paused', tone: 'neutral', icon: 'fa-circle-pause' }
});

export function statusPresentation(status) {
  return SCRIPT_STATUS[status] ?? { label: 'Unknown', tone: 'unknown', icon: 'fa-question' };
}

/** Workers may still be sending: worth polling quickly and offering Kill. */
export function isActiveStatus(status) {
  return status === 'running' || status === 'stopping';
}

/** Kill is only meaningful while the campaign is actually running. */
export function canOfferKill(row, pendingRefs = new Set()) {
  return Boolean(row) && row.canKill === true && row.status === 'running' && !pendingRefs.has(row.campaignRef);
}

/** How many lines the panel keeps. Older ones fall off the top. */
export const MAX_LOG_LINES = 5000;

function timeOf(line) {
  const t = Date.parse(line.time ?? '');
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
}

/**
 * Merges new lines into the buffer: de-duplicated by id, ordered by time (then by the
 * server's sequence, so lines with the same timestamp keep their order), capped.
 *
 * Lifecycle events are re-sent on every poll and log entries are only sent once, so
 * the id is what keeps a line from appearing twice.
 */
export function mergeLogLines(existing, incoming, max = MAX_LOG_LINES) {
  if (!incoming || incoming.length === 0) return existing;

  const seen = new Set(existing.map((line) => line.id));
  const fresh = incoming.filter((line) => line && line.id && !seen.has(line.id));
  if (fresh.length === 0) return existing;

  const merged = [...existing, ...fresh].sort((a, b) => {
    const dt = timeOf(a) - timeOf(b);
    if (dt !== 0 && Number.isFinite(dt)) return dt;
    return (a.seq ?? -1) - (b.seq ?? -1);
  });

  return merged.length > max ? merged.slice(merged.length - max) : merged;
}

/** "10:04:07" in 24-hour local time, or '' when the line has no timestamp. */
export function formatLogTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-GB', { hour12: false });
}
